import { ReactNode, useCallback } from 'react';
import classNames from 'classnames';

import Arrows from 'assets/icons/arrows.svg';
import Message from 'types/Message';
import Container from '@components/atoms/Container';
import Link from '@components/atoms/Link';
import Typography from '@components/atoms/Typography';
import HtmlPreview from '@components/HtmlPreview';
import Truncate from '@templates/Truncate';
import { useIntl } from 'utils/intl';
import { scrollTo } from 'utils/scroll';
import getPlainText from 'utils/getPlainText';
import defer from 'utils/defer';
import useHash from 'hooks/useHash';
import useAnchorState from 'hooks/useAnchorState';

import messages from './StickyMenu.messages';
import classes from './StickyMenu.module.scss';

type Props<SectionId extends string> = {
  name: string;
  secondName?: Message;
  additionalContent?: ReactNode;
  isHide?: boolean;
  labels: Record<SectionId, Message>;
  sections: readonly SectionId[];
  sectionsVisibility?: Partial<Record<SectionId, boolean>>;
  children: ReactNode;
  onClick?: (sectionId: SectionId) => void;
};

const StickyMenu = <SectionId extends string>({
  name,
  secondName,
  additionalContent,
  isHide,
  labels,
  sections,
  sectionsVisibility = {},
  children,
  onClick,
}: Props<SectionId>): JSX.Element => {
  const { menuItems, activeItemId, setActiveItemId } = useAnchorState({ sections, labels, sectionsVisibility });
  const intl = useIntl();

  const setAnchor = useCallback((anchorId: SectionId) => {
    setActiveItemId(anchorId);
    onClick?.(anchorId);
    defer(() => scrollTo(`[anchor-id="${anchorId}"]`));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useHash(setAnchor);

  return (
    <div className={classNames(classes.wrapper, classes.isSticky, { [classes.hide]: isHide })} data-nosnippet>
      <Container className={classes.wrapperContainer}>
        <div className={classes.menuWrapper}>
          <div className={classes.contentWrapper}>
            <Link href="#pageStart" title={intl.formatMessage(messages.scrollToTop)} className={classes.pageStartLink}>
              <Arrows className={classes.icon} />
            </Link>
            <Typography className={classes.name} variant="body1" renderAs="div">
              <Truncate maxWidth={250} title={getPlainText(name)}>
                <HtmlPreview htmlText={name} component="span" />
              </Truncate>
              {secondName}
            </Typography>
            <ul className={classes.list}>
              {menuItems.map(({ id, label }) => (
                <li key={id}>
                  <span
                    className={classNames(classes.listItem, {
                      [classes.active]: activeItemId === id,
                    })}
                    onClick={() => setAnchor(id)}
                  >
                    {label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className={classes.container}>{children}</div>
        </div>
        {additionalContent}
      </Container>
    </div>
  );
};

export default StickyMenu;
